import { useState } from 'react';
import { FaVolumeMute, FaVolumeUp, FaMusic, FaUndo } from 'react-icons/fa';
import soundService from '@/services/sounds';

interface SoundSettingsProps {
  title?: string;
  description?: string;
}

export default function SoundSettings({
  title = 'Sound Notifications',
  description = 'Configure audio notifications for chat events',
}: SoundSettingsProps) {
  const [soundsEnabled, setSoundsEnabled] = useState(soundService.areSoundsEnabled());
  const [volume, setVolume] = useState(soundService.getVolume());
  const [silentMode, setSilentMode] = useState(soundService.getSettings().silentMode);
  const [notificationSoundsEnabled, setNotificationSoundsEnabled] = useState(
    !soundService.getSettings().muteNotifications,
  );
  const [voiceEventSoundsEnabled, setVoiceEventSoundsEnabled] = useState(
    !soundService.getSettings().muteVoiceEvents,
  );

  const handleToggleSounds = () => {
    const newState = !soundsEnabled;
    setSoundsEnabled(newState);
    soundService.toggleSounds(newState);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    soundService.setVolume(newVolume);
    // Play a sample sound to preview volume
    soundService.play('ding');
  };

  const handleToggleNotificationSounds = () => {
    const newState = !notificationSoundsEnabled;
    setNotificationSoundsEnabled(newState);
    soundService.toggleNotificationSounds(newState);
  };

  const handleToggleVoiceEventSounds = () => {
    const newState = !voiceEventSoundsEnabled;
    setVoiceEventSoundsEnabled(newState);
    soundService.toggleVoiceEventSounds(newState);
  };

  const handleToggleSilentMode = () => {
    const newState = !silentMode;
    setSilentMode(newState);
    soundService.toggleSilentMode(newState);
  };

  const handleResetToDefaults = () => {
    if (confirm('Reset all sound settings to server defaults?')) {
      soundService.resetToDefaults();
      setSoundsEnabled(soundService.areSoundsEnabled());
      setVolume(soundService.getVolume());
      setSilentMode(soundService.getSettings().silentMode);
      setNotificationSoundsEnabled(!soundService.getSettings().muteNotifications);
      setVoiceEventSoundsEnabled(!soundService.getSettings().muteVoiceEvents);
    }
  };

  const testSound = (type: 'message' | 'mention' | 'join' | 'notification' | 'ding') => {
    soundService.play(type);
  };

  return (
    <div className="space-y-4">
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-brand-on-background dark:text-[#e0e7ff] flex items-center gap-2">
            <FaMusic className="text-brand-primary dark:text-brand-primary" />
            {title}
          </h3>
          {description && (
            <p className="text-sm text-brand-on-surface-variant dark:text-[#c4b5fd] mt-1">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Silent mode */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
              Silent Mode
            </label>
            <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-0.5">
              Completely mute all notifications (overrides all settings)
            </p>
          </div>
          <button
            onClick={handleToggleSilentMode}
            type="button"
            role="switch"
            aria-checked={silentMode}
            aria-label={silentMode ? 'Disable silent mode' : 'Enable silent mode'}
            className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 ${
              silentMode
                ? 'bg-red-500 shadow-md shadow-red-500/30'
                : 'bg-white/15 border border-white/20 hover:bg-white/20'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                silentMode ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Master sound toggle */}
      <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
              Enable Sounds
            </label>
            <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-0.5">
              Turn audio notifications on or off
            </p>
          </div>
          <button
            onClick={handleToggleSounds}
            type="button"
            role="switch"
            aria-checked={soundsEnabled}
            aria-label={soundsEnabled ? 'Disable UI sounds' : 'Enable UI sounds'}
            className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 ${
              soundsEnabled
                ? 'bg-brand-primary shadow-md shadow-amber-500/30'
                : 'bg-white/15 border border-white/20 hover:bg-white/20'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                soundsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {soundsEnabled && (
        <>
          {/* Volume control */}
          <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
            <div className="mb-3">
              <label className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff] flex items-center gap-2">
                {volume === 0 ? <FaVolumeMute className="text-red-400" /> : <FaVolumeUp className="text-primary-400" />}
                Volume: <span className="text-primary-400 font-bold">{volume}%</span>
              </label>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
              className="w-full h-2.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-amber-500 hover:accent-amber-400 focus:outline-none transition-all"
            />
            <div className="flex justify-between text-xs text-brand-outline dark:text-[#c4b5fd] mt-2 font-medium">
              <span>Quiet</span>
              <span>Loud</span>
            </div>
          </div>

          {/* Notification sounds */}
          <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
                  Notification Sounds
                </label>
                <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-0.5">
                  Sounds for mentions and new messages
                </p>
              </div>
              <button
                onClick={handleToggleNotificationSounds}
                type="button"
                role="switch"
                aria-checked={notificationSoundsEnabled}
                aria-label={
                  notificationSoundsEnabled
                    ? 'Disable notification sounds'
                    : 'Enable notification sounds'
                }
                className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 ${
                  notificationSoundsEnabled
                    ? 'bg-brand-primary shadow-md shadow-amber-500/30'
                    : 'bg-white/15 border border-white/20 hover:bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    notificationSoundsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {notificationSoundsEnabled && (
              <div className="flex gap-2 flex-wrap pt-1">
                <button
                  onClick={() => testSound('mention')}
                  className="btn btn-primary text-xs px-3.5 py-1.5 rounded-full font-semibold shadow-sm"
                >
                  Test Mention
                </button>
                <button
                  onClick={() => testSound('message')}
                  className="btn btn-primary text-xs px-3.5 py-1.5 rounded-full font-semibold shadow-sm"
                >
                  Test Message
                </button>
                <button
                  onClick={() => testSound('notification')}
                  className="btn btn-primary text-xs px-3.5 py-1.5 rounded-full font-semibold shadow-sm"
                >
                  Test Notification
                </button>
              </div>
            )}
          </div>

          {/* Voice event sounds */}
          <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
                  Voice Event Sounds
                </label>
                <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-0.5">
                  Sounds for users joining, leaving, muting
                </p>
              </div>
              <button
                onClick={handleToggleVoiceEventSounds}
                type="button"
                role="switch"
                aria-checked={voiceEventSoundsEnabled}
                aria-label={
                  voiceEventSoundsEnabled
                    ? 'Disable voice event sounds'
                    : 'Enable voice event sounds'
                }
                className={`relative inline-flex h-7 w-12 items-center rounded-full p-1 transition-all cursor-pointer select-none active:scale-95 ${
                  voiceEventSoundsEnabled
                    ? 'bg-brand-primary shadow-md shadow-amber-500/30'
                    : 'bg-white/15 border border-white/20 hover:bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    voiceEventSoundsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {voiceEventSoundsEnabled && (
              <div className="flex gap-2 flex-wrap pt-1">
                <button
                  onClick={() => testSound('join')}
                  className="btn btn-secondary text-xs px-3.5 py-1.5 rounded-full font-semibold border border-green-500/40 text-green-300 hover:bg-green-500/20 shadow-sm"
                >
                  Test Join
                </button>
                <button
                  onClick={() => testSound('ding')}
                  className="btn btn-secondary text-xs px-3.5 py-1.5 rounded-full font-semibold border border-green-500/40 text-green-300 hover:bg-green-500/20 shadow-sm"
                >
                  Test Mute
                </button>
              </div>
            )}
          </div>

          {/* Reset button */}
          <button
            onClick={handleResetToDefaults}
            className="btn btn-secondary w-full py-2.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2"
          >
            <FaUndo aria-hidden="true" size={12} />
            Reset to Server Defaults
          </button>

          {/* Info box */}
          <div className="bg-brand-primary/10 dark:bg-brand-primary/20 border border-brand-primary/30 dark:border-brand-primary/30 rounded-lg p-3">
            <p className="text-xs text-brand-primary dark:text-blue-200">
              💡 Use test buttons to preview sounds at your current volume setting. Adjust volume if
              needed.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
